import React, { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import Modal from "@/components/Modal";
import TemplateTable from "./components/TemplateTable";
import TemplateEditModal from "./components/TemplateEditModal";
import {
  getEmailTemplatesService,
  getEmailTemplateByIdService,
  updateEmailTemplateService,
  type EmailTemplateListItem,
  type EmailTemplateDetail,
  type EmailTemplateUpdatePayload,
} from "@/service/emailTemplate.service";
import { successToast, errorToast } from "@/utils/toast";

const EmailTemplatePage: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDetail, setSelectedDetail] =
    useState<EmailTemplateDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getEmailTemplatesService();
      setTemplates(res.data?.data ?? []);
    } catch {
      errorToast("Error", "Failed to load email templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const openTemplate = async (
    tpl: EmailTemplateListItem,
    isView: boolean
  ) => {
    try {
      setDetailLoading(true);
      setViewMode(isView);
      const res = await getEmailTemplateByIdService(tpl.id);
      setSelectedDetail(res.data?.data ?? null);
    } catch {
      errorToast("Error", "Failed to load template details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleView = (tpl: EmailTemplateListItem) => openTemplate(tpl, true);
  const handleEdit = (tpl: EmailTemplateListItem) => openTemplate(tpl, false);

  const handleClose = () => {
    setSelectedDetail(null);
    setViewMode(false);
  };

  const handleSave = async (payload: EmailTemplateUpdatePayload) => {
    if (!selectedDetail) return;
    try {
      setSaving(true);
      await updateEmailTemplateService(selectedDetail.id, payload);
      successToast("Success", "Template updated successfully");
      handleClose();
      fetchTemplates();
    } catch {
      // Error toast is handled by the global Axios interceptor (includes API body/detail/message)
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout title="Email template" contentClassName="min-h-[90%]">
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-sm text-[#4B5563]">Loading templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-sm text-[#4B5563]">No templates found</p>
          </div>
        ) : (
          <TemplateTable
            templates={templates}
            onView={handleView}
            onEdit={handleEdit}
          />
        )}
      </div>

      <Modal
        isOpen={!!selectedDetail || detailLoading}
        onClose={handleClose}
        title={viewMode ? "View template" : "Edit template"}
        width="min(95%, 868px)"
        contentClassName="p-6 max-h-[calc(100vh-100px)] overflow-y-auto"
      >
        {detailLoading ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm text-[#4B5563]">Loading...</p>
          </div>
        ) : (
          selectedDetail && (
            <TemplateEditModal
              template={selectedDetail}
              viewMode={viewMode}
              saving={saving}
              onCancel={handleClose}
              onSave={handleSave}
            />
          )
        )}
      </Modal>
    </AppLayout>
  );
};

export default EmailTemplatePage;
